import { resolveEngineFrame, type AbilityUseReaders } from '../actions/ability-use.js';
import { MASTERY_POOL, masteryMarkerPreDraw, touchMasteryMarker } from '../ability/charges/mastery-marker.js';
import { HandBack, ecId, type Row, type JanusCtx } from '../ability/context.js';
import { boundaryVerdict, type GmBoundaryVerdict } from '../statuses/gm-boundary.js';
import { statusItemId, statusRemovalBefore } from '../statuses/expiry.js';
import { setAbilityCharges, type ChargeResolution } from './charge-adjust.js';

export const CHARGE_MARKER_VERSION = 'janus.charge.marker.v1';
export interface ChargeMarkerDecision { version: typeof CHARGE_MARKER_VERSION; verdict: GmBoundaryVerdict;
  status_before: Array<{ target: string; item_id: string; status_ids: string[] }> }
export type ResolvedCharge = Extract<ChargeResolution, { supported: true }>;
export const chargeMarkerDue = (charge: ResolvedCharge): boolean => charge.kind !== 'inventory' && charge.itemId === MASTERY_POOL
  && (charge.kind === 'adjust' ? charge.delta !== 0 : charge.after !== charge.before);
const emptyVerdict = (): GmBoundaryVerdict => ({ rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], charges: [], playerCharactersChanged: [] });
export const emptyChargeMarkerDecision = (): ChargeMarkerDecision => ({ version: CHARGE_MARKER_VERSION, verdict: emptyVerdict(), status_before: [] });

/** Manual charge writes use the native marker/status executor, including its gain arms and reentrancy latch.
 * The existing engine frame owns lazy snapshots and pre-draw proofs; no page row is changed while planning. */
export function planChargeMarker(charge: ResolvedCharge, actorId: string, encounter: Row, members: ReadonlyMap<string, Row>,
  read: AbilityUseReaders, roll: JanusCtx['roll']): { supported: true; after: number; decision: ChargeMarkerDecision; fortunateOffers: string[] }
    | { supported: false; reason: string } {
  if (!chargeMarkerDue(charge)) return { supported: true, after: charge.after,
    decision: emptyChargeMarkerDecision(), fortunateOffers: [] };
  const holder = (ctx: JanusCtx): Row => {
    const row = ctx.members.get(actorId);
    if (!row) throw new HandBack('charge-marker:holder-not-held');
    return row;
  };
  const run = resolveEngineFrame({}, encounter, members, read, roll,
    ctx => masteryMarkerPreDraw(ctx, holder(ctx)), ctx => {
      const row = holder(ctx);
      setAbilityCharges(row, charge.itemId, charge.after, read);
      ctx.chargesChanged.push({ holder: actorId, item_id: charge.itemId, before: charge.before, charges: charge.after });
      touchMasteryMarker(ctx, row);
      // The charge lane can save the ordinary boundary rows below. Keep unrecorded followups pre-draw.
      if (ctx.inventoryChanged.length || ctx.spawnRows || ctx.summons?.length || ctx.turnTransition || ctx.deathTail?.length
          || ctx.concentrationBreaks?.length)
        throw new HandBack('charge-marker:gain-followup-not-recorded');
    });
  if (run.kind !== 'resolved') return { supported: false, reason: run.reason };
  const verdict = boundaryVerdict(run.ctx);
  const primary = verdict.charges.filter(c => c.holder === actorId && c.item_id === charge.itemId).pop()!;
  verdict.charges = verdict.charges.filter(c => c.holder !== actorId || c.item_id !== charge.itemId);
  for (const removal of verdict.statusesRemoved) {
    const owner = members.get(removal.target);
    const stored = owner && read.statuses(owner).find(s => String(s.status_id).toUpperCase() === removal.status_id);
    if (!stored) return { supported: false, reason: 'charge-marker:status-before-not-held' };
    removal.before = statusRemovalBefore(stored, ecId(owner!));
  }
  const statusBefore = verdict.statusesAdded.map(added => ({ target: added.target, item_id: added.status_item_id,
    status_ids: read.statuses(members.get(added.target)!).filter(status => statusItemId(status) === added.status_item_id)
      .map(status => String(status.status_id).toUpperCase()).sort() }));
  return { supported: true, after: primary.charges, decision: { version: CHARGE_MARKER_VERSION, verdict, status_before: statusBefore },
    fortunateOffers: [...(run.ctx.fortunateOffers ?? [])] };
}
